import React, { useState, useEffect } from "react";
import styled from '@emotion/styled';
import { Popover, IconButton, Button, Divider, Switch, FormControlLabel } from "@mui/material";

import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import PreviewRoundedIcon from '@mui/icons-material/PreviewRounded';
import DisabledVisibleRoundedIcon from '@mui/icons-material/DisabledVisibleRounded';
import CollectionsBookmarkRoundedIcon from '@mui/icons-material/CollectionsBookmarkRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';

import useStylistsStore from "../../../store/stylists";

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { getStylistView, saveStylistView } from "../apiService";

import useSnackbarStore from "../../../store/snackbar";
import { BasicFlex, LabelUpper } from "../../../utils/Common";

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  flex-grow: 1;
  align-items: center;
  gap: 1.5rem;
  padding: .6rem 1rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  cursor: pointer;
  opacity: ${props => (props.$dragging ? 0.4 : 1)};
  background-color: ${props => (props.$dragging ? props.theme.colors.secondaryFaint : 'transparent')};
  transition: opacity .15s ease, background-color .15s ease;

  &:hover {
    background-color: ${props => props.theme.colors.secondaryFaint};
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: .75rem;
  min-width: 0;
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  color: #9e9e9e;
  cursor: grab;
  user-select: none;
  touch-action: none;

  &:hover {
    color: #616161;
  }

  &:active {
    cursor: grabbing;
  }
`;

const BottomButton = styled.div`
  padding: .5rem;
`

const mergeStylistList = (ol, nl) => {
  if (!ol?.length) return nl;
  return ol.map(o => {
    const n = nl.find(n => n.stylist_id === o.stylist_id);
    return {
      ...o,
      checked: n && n.checked ? true : false
    }
  })
}

const SelectStylist = () => {
  const activeStylists = useStylistsStore((state) => state.activeStylists);
  const tempActiveStylists = useStylistsStore((state) => state.tempActiveStylists);
  const setActiveStylists = useStylistsStore((state) => state.setActiveStylists);
  const setTempActiveStylists = useStylistsStore((state) => state.setTempActiveStylists);
  const handleCheckStylists = useStylistsStore((state) => state.handleCheckStylists);
  const handleCheckAll = useStylistsStore((state) => state.handleCheckAll);

  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
  
  const [anchorEl, setAnchorEl] = useState(null);

  const queryClient = useQueryClient();

  const [toggleAll, setToggleAll] = useState(false);

  // Drag-and-drop state:
  // grabbedIndex -> which row's handle is currently held (only that row is draggable)
  // draggedIndex -> which row is actively being dragged (used for live reordering + styling)
  const [grabbedIndex, setGrabbedIndex] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const handleClick = (e) => {
    setAnchorEl(e.currentTarget);
  }

  const handleClose = () => {
    setAnchorEl(null);
  }

  const updateView = () => {
    setActiveStylists(tempActiveStylists.map(stylist => ({ ...stylist })));
    handleClose();
  }

  const cancelView = () => {
    setTempActiveStylists(activeStylists.map(stylist => ({ ...stylist })));
    handleClose();
  }

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Some browsers require data to be set for the drag to initiate.
    try { e.dataTransfer.setData('text/plain', String(index)); } catch (_) {}
  }

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex === null || draggedIndex === index) return;

    // Live reorder: move the dragged row into the hovered position as you drag.
    const updated = [...tempActiveStylists];
    const [moved] = updated.splice(draggedIndex, 1);
    updated.splice(index, 0, moved);
    setTempActiveStylists(updated);
    setDraggedIndex(index);
  }

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setGrabbedIndex(null);
  }

  const updateMutation = useMutation({
    mutationFn: () => saveStylistView(tempActiveStylists),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings", "calendar", "stylist_view"] });
      showSnackbar('Updated successfully!', 'success');
      handleClose();
    },
    onError: () => {
      cancelView();
      showSnackbar('Failed to update!', 'error');
    }
  })


  return (
    <>
    <IconButton onClick={handleClick}>
      <PreviewRoundedIcon color="basic" />
    </IconButton>
      <Popover
        id="menu-appbar"
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: "30rem",
              maxWidth: "35rem",
            },
          }
        }}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
      >
      <BasicFlex mx="1.2rem" my=".5rem" width="auto">
        <LabelUpper $size="1.2rem" $light>Stylist List</LabelUpper>
        <IconButton onClick={cancelView} size="small">
          <CloseRoundedIcon size="small" />
        </IconButton>
      </BasicFlex>
      <Divider />
      {tempActiveStylists?.map((stylist, index) => (
        <Item
          key={stylist.stylist_id ?? index}
          $dragging={draggedIndex === index}
          draggable={grabbedIndex === index}
          onDragStart={(e) => handleDragStart(e, index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDragEnd={handleDragEnd}
          onClick={() => handleCheckStylists(index)}
        >
          <Left>
            <DragHandle
              onMouseDown={() => setGrabbedIndex(index)}
              onMouseUp={() => setGrabbedIndex(null)}
              onClick={(e) => e.stopPropagation()}
            >
              <DragIndicatorRoundedIcon fontSize="small" />
            </DragHandle>
            <div>{stylist.full_name}</div>
          </Left>
          <div>
            <IconButton>
              {stylist?.checked ? <VisibilityRoundedIcon color="primary" /> : <VisibilityOffRoundedIcon />}
            </IconButton>
          </div>
        </Item>
      ))}
      <BasicFlex mx="1.6rem" my="1rem">
        <FormControlLabel control={<Switch onChange={(e) => handleCheckAll(e.target.checked)} size="small" />} label="Toggle All" />
      </BasicFlex>
      <Divider />
      <BottomButton>
        {/* <Button startIcon={<DisabledVisibleRoundedIcon />} onClick={cancelView} variant="outlined" fullWidth sx={{marginBottom: ".8rem"}}>Cancel</Button> */}
        {/* <Button startIcon={<PreviewRoundedIcon />} color="secondary" onClick={updateView} variant="outlined" fullWidth sx={{marginBottom: ".5rem"}}>Update View</Button> */}
        <Button color="secondary" startIcon={<CollectionsBookmarkRoundedIcon />} onClick={updateMutation.mutate} variant="contained" fullWidth>Save Calendar View</Button>
      </BottomButton>
      </Popover>
    </>
  );
};

export default SelectStylist;